import { useState } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import { IconCheck, IconEye, IconHistory } from '@tabler/icons-react';
import { Segmented, useToast } from '@hana/ui';
import { ISSUED_COLS, ISSUED_FILTERS, ISSUED_STATUS, TEMPLATE_CATALOG, TEMPLATE_COLS, TEMPLATE_GROUPS, contractKey, issuedAction, type ContractKey, type TemplateGroup } from './contract';
import type { LaborContract } from './types';
import s from './hr.module.css';
import c from './contractTabs.module.css';

// Mint-LaborContract · tab Mẫu hợp đồng + Đã phát hành (code board khối tabTpl / tabIssued). Chỉ đọc; nút ghi báo toast.
const TONE = { ok: s.tOk ?? '', warn: s.tWarn ?? '', mute: s.tMute ?? '' };

export function ContractTemplates() {
  const t = useT();
  const toast = useToast();
  const [group, setGroup] = useState<TemplateGroup | 'all'>('hdld');
  const rows = TEMPLATE_CATALOG.filter((r) => group === 'all' || r.group === group);
  const items = TEMPLATE_GROUPS.map(([key, label]) => ({
    key,
    label: `${label} ${key === 'all' ? TEMPLATE_CATALOG.length : TEMPLATE_CATALOG.filter((r) => r.group === key).length}`,
  }));
  return (
    <>
      <div className={c.bar}>
        <Segmented kind="toggle" label="Nhóm mẫu" value={group} onChange={(k) => setGroup(k as TemplateGroup | 'all')} items={items} />
        <span>{rows.length} mẫu</span>
      </div>
      <section className={s.card} aria-label="Mẫu hợp đồng" style={{ padding: 0 }}>
        <table className={`${s.table} ${c.tbl}`}>
          <colgroup>{TEMPLATE_COLS.map(([k, w], i) => <col key={`${k}${i}`} style={{ width: w }} />)}</colgroup>
          <thead><tr>{TEMPLATE_COLS.map(([k], i) => <th key={`${k}${i}`} scope="col">{k}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td><b>{r.code}-{r.version}</b><div>{r.name}</div></td>
                <td>{r.type}</td>
                <td>{r.term}</td>
                <td>{r.version}</td>
                <td className={c.law} title={r.law}>{r.law}</td>
                <td><span className={`${s.stChip} ${TONE.mute}`}>Draft</span></td>
                <td>
                  <div className={c.acts}>
                    <button type="button" className={c.btn} onClick={() => toast({ msg: `Mở mẫu ${r.code} bằng FilePreview` })}><IconEye size={16} stroke={1.8} aria-hidden="true" />Xem</button>
                    <button type="button" className={c.btn} onClick={() => toast({ msg: t('hr.s.noWrite') })}><IconHistory size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.newVer')}</button>
                    <button type="button" className={`${c.btn} ${c.btnPri}`} onClick={() => toast({ msg: t('hr.s.noWrite') })}><IconCheck size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.approveUse')}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

export function ContractIssued({ contracts, onRenew, onTemplates }: { contracts: readonly LaborContract[]; onRenew: () => void; onTemplates: () => void }) {
  const t = useT();
  const toast = useToast();
  const [f, setF] = useState<ContractKey | 'all'>('all');
  const keyed = contracts.map((r) => ({ r, key: contractKey(r) }));
  const rows = keyed.filter((x) => f === 'all' || x.key === f);
  const items = ISSUED_FILTERS.map(([key, label]) => ({ key, label: `${label} ${key === 'all' ? keyed.length : keyed.filter((x) => x.key === key).length}` }));
  const act = (key: ContractKey, no: string) => {
    if (key === 'soon') onRenew();
    else if (key === 'draft') onTemplates();
    else if (key === 'end') toast({ msg: 'Mở HR-NGHIVIEC-2026-001 ở Quyết định nhân sự' });
    else toast({ msg: `${no} · chưa mở ghi ở bản này` });
  };
  return (
    <>
      <div className={c.bar}>
        <Segmented kind="toggle" label="Lọc trạng thái" value={f} onChange={(k) => setF(k as ContractKey | 'all')} items={items} />
        <span>{rows.length} hợp đồng</span>
      </div>
      <section className={s.card} aria-label="Hợp đồng đã phát hành" style={{ padding: 0 }}>
        <table className={`${s.table} ${c.tbl}`}>
          <colgroup>{ISSUED_COLS.map(([k, w], i) => <col key={`${k}${i}`} style={{ width: w }} />)}</colgroup>
          <thead><tr>{ISSUED_COLS.map(([k], i) => <th key={`${k}${i}`} scope="col">{k}</th>)}</tr></thead>
          <tbody>
            {rows.map(({ r, key }) => {
              const st = ISSUED_STATUS[key];
              const a = issuedAction(key);
              const soon = r.daysLeft !== null && r.daysLeft <= 30;
              const left = r.daysLeft === null ? (key === 'end' ? 'chấm dứt 31/Oct/2026' : '') : `còn ${r.daysLeft} ngày`;
              return (
                <tr key={r.no}>
                  <td><button type="button" className={c.noLink} title={`Mở chi tiết ${r.no}`} onClick={() => toast({ msg: `Chi tiết ${r.no} chưa mở ở bản này` })}>{r.no}</button><div>{r.template}</div></td>
                  <td>{r.name}<div>{r.user} · {r.seat}</div></td>
                  <td>{r.kindLabel}<div>ký lần {r.nth}</div></td>
                  <td>{r.from} → {r.to}<div className={soon ? c.warnTx : undefined}>{left}</div></td>
                  <td><span className={`${s.stChip} ${TONE[st.tone]}`}>{st.label}</span></td>
                  <td>
                    <div className={c.acts}>
                      <button type="button" className={c.btn} onClick={() => toast({ msg: `Mở ${r.no} bằng FilePreview` })}><IconEye size={16} stroke={1.8} aria-hidden="true" />Xem</button>
                      {a && <button type="button" className={`${c.btn} ${c.btnSoft}`} onClick={() => act(key, r.no)}>{a}</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div className={c.empty}>{t('hr.s.noContractMatch')}</div>}
      </section>
    </>
  );
}
